import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import GuardianLinksModal from '../components/GuardianLinksModal';
import AdminPage, { AdminWindowSwitcher, personTarget } from '../components/AdminShell';

const ROLE_LABELS = {
  athlete: 'Athlete',
  base_user: 'Base User',
  player_parent: 'Athlete',
  coach: 'Coach',
  basic_coach: 'Coach (Pending)',
  partner: 'Partner',
  guardian: 'Guardian',
  admin: 'Admin',
  internal_staff: 'Internal Staff',
  marketing: 'Marketing',
};

// user_source values (see User.user_source). Unknown values fall back to the
// raw string so a new source shows up rather than silently reading as organic.
const SOURCE_LABELS = {
  prozpects_user: 'Self sign-up',
  concierge: 'Concierge',
  admin_inputted: 'Admin-created',
};
const sourceLabel = (s) => (!s ? 'Self sign-up' : (SOURCE_LABELS[s] || s));

// Windows the report offers, in display order. `value` is the day count the
// API accepts; `label` is what the pill shows. The shared AdminWindowSwitcher
// reads {value,label}, hence `value` rather than the old `days` key.
const WINDOWS = [
  { value: 1,  label: '24 hours' },
  { value: 7,  label: '7 days' },
  { value: 30, label: '30 days' },
];

// Server timestamps are UTC but serialized without a tz suffix. Append 'Z' so
// JS parses as UTC; toLocale* then renders in the viewer's local timezone.
const parseServerDate = (iso) => {
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = parseServerDate(iso);
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
};

// Sort helper that puts NULLs last for both directions.
const compareNullsLast = (a, b, dir) => {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') {
    return dir === 'asc' ? a - b : b - a;
  }
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
};

const COLUMNS = [
  { key: 'name',           label: 'User',          accessor: (u) => `${u.last_name || ''} ${u.first_name || ''}`.trim().toLowerCase() || (u.email || '').toLowerCase() },
  { key: 'role',           label: 'Role',          accessor: (u) => ROLE_LABELS[u.role] || u.role || '' },
  { key: 'user_source',    label: 'Source',        accessor: (u) => sourceLabel(u.user_source) },
  { key: 'created_at',     label: 'Signed up',     accessor: (u) => u.created_at || null },
  { key: 'is_email_verified', label: 'Email verified', accessor: (u) => (u.is_email_verified ? 1 : 0), align: 'center' },
  { key: 'first_login_at', label: 'First login',   accessor: (u) => u.first_login_at || null },
];

// `embedded` renders the report body WITHOUT the admin shell. /admin/reports
// mounts this same component as its "Signups" tab (AdminReportsPage.js), and
// that page has already drawn the sidebar and header — nesting a second .adm
// shell inside it would double the rail and the page background.
export default function AdminSignupsReportPage({ embedded = false }) {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(1);
  const [signups, setSignups] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Newest signup first — admins land here to see who just joined.
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [guardianModal, setGuardianModal] = useState(null);

  const getConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'internal_staff') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchSignups = useCallback(async (windowDays) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/admin/reports/signups?days=${windowDays}`,
        getConfig()
      );
      setSignups(res.data?.signups || []);
      setCount(res.data?.count || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load signups');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getConfig]);

  useEffect(() => {
    fetchSignups(days);
  }, [fetchSignups, days]);

  const sortedSignups = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey);
    if (!col) return signups;
    const copy = [...signups];
    copy.sort((a, b) => compareNullsLast(col.accessor(a), col.accessor(b), sortDir));
    return copy;
  }, [signups, sortKey, sortDir]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      const isTextCol = key === 'name' || key === 'role' || key === 'user_source';
      setSortDir(isTextCol ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key) => (
    key === sortKey
      ? <span className="adm-sort adm-sort--on">{sortDir === 'asc' ? '↑' : '↓'}</span>
      : <span className="adm-sort">↕</span>
  );

  const windowLabel = WINDOWS.find((w) => w.value === days)?.label || `${days} days`;

  const body = (
    <>
      <div className="adm__card adm__card--compact">
        <div className="adm-headline">
          <span className="adm-headline__num">{count}</span>
          <span className="adm-headline__label">
            new {count === 1 ? 'signup' : 'signups'} in the last {windowLabel}
          </span>
        </div>
      </div>

      {error && <div className="adm-error">{error}</div>}

      {loading ? (
        <div className="adm__card"><div className="adm-loading">Loading signups…</div></div>
      ) : (
        <div className="adm__card adm__card--flush">
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`adm-th--sortable${c.align === 'center' ? ' adm-th--center' : ''}`}
                      onClick={() => handleSort(c.key)}
                      title="Click to sort"
                      aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      {c.label}{sortIndicator(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedSignups.length === 0 && (
                  <tr><td colSpan={COLUMNS.length}><div className="adm-empty">No signups in the last {windowLabel}.</div></td></tr>
                )}
                {sortedSignups.map((u) => {
                  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                  // Coach / partner / staff rows are NOT links: they have no
                  // athlete profile and no linked athletes, and this cell used
                  // to send them to /admin/athletes/{id} — an error page.
                  const target = personTarget(u.role);
                  return (
                    <tr key={u.id}>
                      <td>
                        {target ? (
                          <button
                            type="button"
                            className="adm-link adm-cell__name"
                            onClick={() => (target === 'guardian'
                              ? setGuardianModal(u)
                              : navigate(`/athletes/${u.id}`))}
                            title={target === 'guardian'
                              ? `View athletes linked to ${name}`
                              : `View ${name}'s full profile`}
                          >
                            {name}
                            {target === 'guardian' && <span className="adm-chip adm-chip--amber" style={{ marginLeft: 6 }}>linked athletes</span>}
                          </button>
                        ) : (
                          <span className="adm-cell__name">{name}</span>
                        )}
                        <div className="adm-cell__meta">{u.email}</div>
                      </td>
                      <td>{ROLE_LABELS[u.role] || u.role}</td>
                      <td>{sourceLabel(u.user_source)}</td>
                      <td title={u.created_at || ''}>{formatDateTime(u.created_at) || '—'}</td>
                      <td className="adm-td--center">
                        {u.is_email_verified
                          ? <span className="adm-yes">✓</span>
                          : <span className="adm-no">—</span>}
                      </td>
                      <td title={u.first_login_at || ''}>
                        {formatDateTime(u.first_login_at) || <span className="adm-never">Never</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <GuardianLinksModal
        guardian={guardianModal}
        onClose={() => setGuardianModal(null)}
        onOpenAthlete={(athleteId) => { setGuardianModal(null); navigate(`/athletes/${athleteId}`); }}
      />
    </>
  );

  const switcher = (
    <AdminWindowSwitcher options={WINDOWS} value={days} onChange={setDays} />
  );

  if (embedded) {
    return (
      <>
        <div className="adm-toolbar">
          <div className="adm-toolbar__spacer" />
          {switcher}
        </div>
        {body}
      </>
    );
  }

  return (
    <AdminPage
      active="reports"
      title="Signups"
      subtitle="Who registered, where they came from, and whether they ever came back."
      actions={switcher}
    >
      {body}
    </AdminPage>
  );
}
